import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

export interface ContaDespesas {
  id: number;
  descricao?: string;
  referencia?: string;
  contaContabilId?: number | null;
  centroCustoId?: number | null;
  grupoDespesasId?: number | null;
  isInativo: boolean;
  isDespesa: boolean;
}

export interface ContaDespesasFormData {
  descricao?: string;
  referencia?: string;
  contaContabilId?: number | null;
  centroCustoId?: number | null;
  grupoDespesasId?: number | null;
  isInativo?: boolean;
  isDespesa?: boolean;
}

export function useContasDespesas() {
  const queryClient = useQueryClient();

  const { data: contas = [], isLoading } = useQuery({
    queryKey: ["tipos-despesas"],
    queryFn: async () => {
      const { data } = await apiClient.get<ContaDespesas[]>("/api/tipos-despesas");
      return data;
    },
  });

  const createConta = useMutation({
    mutationFn: async (formData: ContaDespesasFormData) => {
      const { data } = await apiClient.post<ContaDespesas>("/api/tipos-despesas", formData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tipos-despesas"] });
      toast.success("Conta criada com sucesso!");
    },
    onError: (error: Error) => {
      toast.error("Erro ao criar conta: " + error.message);
    },
  });

  const updateConta = useMutation({
    mutationFn: async ({ id, ...formData }: ContaDespesasFormData & { id: number }) => {
      const { data } = await apiClient.put<ContaDespesas>(`/api/tipos-despesas/${id}`, formData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tipos-despesas"] });
      toast.success("Conta atualizada com sucesso!");
    },
    onError: (error: Error) => {
      toast.error("Erro ao atualizar conta: " + error.message);
    },
  });

  const deleteConta = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/api/tipos-despesas/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tipos-despesas"] });
      toast.success("Conta inativada com sucesso!");
    },
    onError: (error: Error) => {
      toast.error("Erro ao inativar conta: " + error.message);
    },
  });

  return { contas, isLoading, createConta, updateConta, deleteConta };
}
